import { memo, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import TextButton from 'LuxKit/Button/TextButton'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import Image from 'components/Common/Image'
import NumberRating from 'components/Common/NumberRating'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { TripItineraryData } from 'tripPlanner/api/types'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'

const CARD_IMAGE_SIZE = 120

const CardContainer = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.palette.neutral.default.four};
  }
`

const ImageSection = styled.div`
  position: relative;
  width: ${rem(CARD_IMAGE_SIZE)};
  min-width: ${rem(CARD_IMAGE_SIZE)};
  min-height: ${rem(CARD_IMAGE_SIZE)};
  flex-shrink: 0;
`

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.neutral.default.six};
  display: flex;
  align-items: center;
  justify-content: center;
`

const AddButtonContainer = styled.div`
  position: absolute;
  top: ${rem(4)};
  right: ${rem(4)};
`

const ContentSection = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(8)} ${rem(12)};
  gap: ${rem(8)};
`

interface Props {
  offer: ExploreOffer
  itineraryData?: TripItineraryData
  onSendMessage: (message: string) => void
  onViewOffer: (data: { offerId: string; offerType?: string }) => void
  onHover?: (offerId: string | undefined) => void
  isHighlighted?: boolean
}

function ChatMapOfferCard({
  offer,
  itineraryData,
  onSendMessage,
  onViewOffer,
  onHover = noop,
  isHighlighted,
}: Props) {
  const intl = useIntl()

  const fromLabel = intl.formatMessage({
    id: 'dfcwDcH90Uz5YCe5kG37',
    defaultMessage: 'From',
    description: 'Price caption prefix when no night count is available on the map offer card',
  })
  const viewLabel = intl.formatMessage({
    id: 'Oki-KXseqCJeFJ3nRlAT',
    defaultMessage: 'View',
    description: 'Button label to view offer details on the map offer card',
  })

  const priceCaption = useMemo(() => {
    if (offer.numberOfNights) {
      return intl.formatMessage(
        {
          id: '9dgRz0i4IHL0Iu5ubDlb',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Price caption showing number of nights on the map offer card',
        },
        { count: offer.numberOfNights },
      )
    }
    return fromLabel
  }, [offer.numberOfNights, intl, fromLabel])

  const handleMouseEnter = useCallback(() => {
    onHover(offer.id)
  }, [offer.id, onHover])

  const handleMouseLeave = useCallback(() => {
    onHover(undefined)
  }, [onHover])

  const handleViewClick = useCallback(() => {
    onViewOffer({ offerId: offer.id, offerType: offer.offerType })
  }, [offer.id, offer.offerType, onViewOffer])

  const isExperienceOffer = offer.offerType === 'experience'

  return (
    <CardContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ImageSection>
        {offer.imageIds[0] && (
          <Image
            id={offer.imageIds[0]}
            alt={offer.name}
            width={CARD_IMAGE_SIZE}
            height={CARD_IMAGE_SIZE}
            gravity="auto"
            fit="center"
            loading="lazy"
          />
        )}
        {!offer.imageIds[0] && (
          <ImagePlaceholder>
            <SolidBedIcon size="M" colour="neutral-three" />
          </ImagePlaceholder>
        )}
        {isExperienceOffer && itineraryData && (
          <AddButtonContainer>
            <AddToTripAction
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              placeName={offer.name}
              offerId={offer.id}
              offerType={offer.offerType}
              coordinates={offer.coordinates}
              variant="filled"
            />
          </AddButtonContainer>
        )}
        {!isExperienceOffer && (
          <AddButtonContainer>
            <AddHotelToTripButton
              itineraryData={itineraryData}
              hotelName={offer.name}
              offerId={offer.id}
              offerType={offer.offerType}
              hotelCity={offer.location}
              hotelCoordinates={offer.coordinates}
              onSendMessage={onSendMessage}
            />
          </AddButtonContainer>
        )}
      </ImageSection>

      <ContentSection>
        <Group direction="vertical" gap={4}>
          <Group direction="horizontal" gap={8} verticalAlign="start">
            <Heading variant="heading6" lineClamp={2}>{offer.name}</Heading>
            {offer.rating !== undefined && (
              <NumberRating rating={offer.rating} variant="xs" onlyNumber />
            )}
          </Group>
          {offer.description && (
            <BodyText variant="small" colour="neutral-two" lineClamp={3}>
              {offer.description}
            </BodyText>
          )}
          {!offer.description && offer.location && (
            <BodyText variant="small" colour="neutral-three" lineClamp={3}>
              {offer.location}
            </BodyText>
          )}
        </Group>

        <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between">
          <div>
            {offer.price !== undefined && <>
              <PriceRowPriceCaption>{priceCaption}</PriceRowPriceCaption>
              <PriceRowPrice price={offer.price} size="S" saleUnit={offer.pricePer} />
            </>}
          </div>
          <TextButton
            kind={isHighlighted ? 'primary' : 'secondary'}
            size="small"
            onClick={handleViewClick}
          >
            {viewLabel}
          </TextButton>
        </Group>
      </ContentSection>
    </CardContainer>
  )
}

export default memo(ChatMapOfferCard)
